import { ApiError, type components, unwrap } from '@shared/api-client/index';
import { clearPersistedQueryCache } from '@shared/ui/query/query-provider.component';
import { toastApiError } from '@shared/ui/toast/toast-store';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect } from 'react';

import { api } from './api';
import { goToLogin } from './auth';
import { QUERY_CACHE_KEY } from './cache';

export const meKey = ['me'] as const;
const accountsKey = ['accounts'] as const;

/** One account this browser is signed into (for the switcher). */
export type Account = components['schemas']['AccountResponse'];

/**
 * The signed-in principal (user + active-workspace role), or null when logged
 * out — a 401 is the logged-out state, not an error, matching the demo's session
 * hook. Unlike the demo we keep the whole response, since callers need the
 * workspace `role` alongside the user.
 */
export function useMe() {
	return useQuery({
		queryKey: meKey,
		queryFn: async () => {
			try {
				return unwrap(await api.GET('/v1/me'));
			} catch (error) {
				if (error instanceof ApiError && error.status === 401) return null;
				throw error;
			}
		},
		// Revalidate on mount: in local dev the api cookie isn't on the app origin,
		// so the first load can come back null until the client refetches.
		staleTime: 0,
	});
}

/**
 * Redirect signed-out users to the auth app. `useMe` resolves to `null` on a 401
 * (never throws), so this runs client-side after the session check settles and
 * doesn't block SSR — the page still server-renders its content for signed-in
 * requests (e.g. a shared task link), and only bounces once we know we're out.
 */
export function useRequireSession(): void {
	const { data: me } = useMe();
	useEffect(() => {
		if (me === null) {
			goToLogin();
		}
	}, [me]);
}

/** The accounts this browser is signed into, active first — drives the switcher. */
export function useAccounts() {
	return useQuery({
		queryKey: accountsKey,
		queryFn: async () => unwrap(await api.GET('/v1/auth/accounts')),
		staleTime: 0,
	});
}

/**
 * Switch the active account. The server just swaps the session cookie, so drop every
 * cached query (this account's data is stale under the new cookie) and hard-navigate to
 * the root, landing on the switched-to account's own default board.
 */
export function useSwitchAccount() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (userId: string) =>
			api.POST('/v1/auth/switch-account', { body: { userId } }).then(unwrap),
		onError: toastApiError,
		onSuccess: () => {
			queryClient.clear();
			clearPersistedQueryCache(QUERY_CACHE_KEY);
			window.location.assign('/');
		},
	});
}

/**
 * Sign out the active account. Clears every cached query (in memory + the persisted
 * copy, so the next visitor on a shared browser can't restore ours). If another account
 * is still signed in on this browser (`remaining > 0`, the server promoted it to
 * active), reload into it; otherwise leave for the auth app's login.
 */
export function useSignOut() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => api.POST('/v1/auth/logout').then(unwrap),
		onSuccess: (result) => {
			queryClient.clear();
			clearPersistedQueryCache(QUERY_CACHE_KEY);
			if (result.remaining > 0) {
				window.location.assign('/');
			} else {
				goToLogin();
			}
		},
	});
}
